import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { joinClassNames } from '@vertex-protocol/web-common';
import { DropdownUi, UpDownChevronIcon } from '@vertex-protocol/web-ui';
import { getFundingTokenSymbols } from 'client/modules/collateral/nadoDeposit/config/fundingRoutes';
import { useState } from 'react';

interface Props {
  selectedTokenSymbol: string;
  onTokenSelected: (tokenSymbol: string) => void;
}

/**
 * Token selector dropdown — token-first model.
 *
 * Always enabled. Shows bridge-first funding tokens from getFundingTokenSymbols()
 * regardless of the currently selected chain. Chain selection follows token
 * selection (not the other way around).
 */
export function NadoTokenSelect({ selectedTokenSymbol, onTokenSelected }: Props) {
  const [open, setOpen] = useState(false);

  const tokens = getFundingTokenSymbols();

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          className={joinClassNames(
            'bg-surface-1 border-stroke',
            'flex w-full items-center justify-between rounded-sm border px-3 py-2.5',
            'hover:bg-surface-2 cursor-pointer transition-colors outline-none',
          )}
        >
          <div className="flex items-center gap-x-2.5">
            <TokenIcon symbol={selectedTokenSymbol} />
            <span className="text-text-primary text-sm">{selectedTokenSymbol}</span>
          </div>
          <UpDownChevronIcon open={open} className="text-text-tertiary size-3" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          style={{ zIndex: 60 }}
        >
          <DropdownUi.Content className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
            {tokens.map((symbol) => (
              <DropdownMenu.Item
                key={symbol}
                className={joinClassNames(
                  'flex cursor-pointer items-center gap-x-2.5 rounded-sm px-3 py-2',
                  'outline-none transition-colors',
                  'data-[highlighted]:bg-overlay-hover',
                  symbol === selectedTokenSymbol
                    ? 'text-text-primary'
                    : 'text-text-secondary',
                )}
                onSelect={() => onTokenSelected(symbol)}
              >
                <TokenIcon symbol={symbol} />
                <span className="text-sm">{symbol}</span>
              </DropdownMenu.Item>
            ))}
          </DropdownUi.Content>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Official Tether USDT/USDT0 token icon. */
export function TokenIcon({ symbol }: { symbol: string }) {
  const isUsdt = symbol === 'USDT' || symbol === 'USDT0';

  if (symbol === 'USDC') {
    // Official Circle USDC brand mark
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 2000 2000"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="size-5 flex-shrink-0"
        aria-label="USDC"
      >
        <circle cx="1000" cy="1000" r="1000" fill="#2775CA" />
        <path d="M1275 1158.33C1275 1016.67 1191.67 966.667 1025 941.667C908.333 921.667 883.333 891.667 883.333 841.667C883.333 791.667 916.667 758.333 983.333 758.333C1041.67 758.333 1075 783.333 1091.67 833.333C1095 841.667 1104.17 850 1112.5 850H1166.67C1179.17 850 1187.5 841.667 1187.5 829.167V825C1175 741.667 1108.33 675 1016.67 662.5V575C1016.67 562.5 1008.33 550 991.667 550H941.667C929.167 550 916.667 558.333 916.667 575V658.333C804.167 675 729.167 758.333 729.167 858.333C729.167 991.667 808.333 1045.83 975 1070.83C1083.33 1091.67 1120.83 1112.5 1120.83 1170.83C1120.83 1229.17 1066.67 1270.83 995.833 1270.83C900 1270.83 866.667 1229.17 854.167 1179.17C850 1166.67 841.667 1158.33 829.167 1158.33H770.833C758.333 1158.33 750 1170.83 750 1183.33V1187.5C766.667 1283.33 829.167 1354.17 920.833 1375V1458.33C920.833 1470.83 929.167 1483.33 945.833 1483.33H995.833C1008.33 1483.33 1020.83 1475 1020.83 1458.33V1375C1133.33 1354.17 1208.33 1266.67 1208.33 1158.33" fill="white" />
        <path d="M787.5 1541.67C491.667 1433.33 337.5 1104.17 450 812.5C508.333 666.667 620.833 558.333 762.5 500C775 495.833 783.333 483.333 783.333 470.833V416.667C783.333 404.167 775 395.833 762.5 391.667C758.333 391.667 754.167 391.667 750 395.833C408.333 516.667 229.167 891.667 350 1233.33C425 1441.67 587.5 1604.17 795.833 1679.17C808.333 1683.33 820.833 1679.17 825 1666.67C829.167 1662.5 829.167 1658.33 829.167 1650V1595.83C829.167 1587.5 816.667 1545.83 787.5 1541.67ZM1250 395.833C1237.5 391.667 1225 395.833 1220.83 408.333C1216.67 412.5 1216.67 416.667 1216.67 425V479.167C1216.67 491.667 1229.17 529.167 1258.33 533.333C1554.17 641.667 1708.33 970.833 1595.83 1262.5C1537.5 1408.33 1425 1516.67 1283.33 1575C1270.83 1579.17 1262.5 1591.67 1262.5 1604.17V1658.33C1262.5 1670.83 1270.83 1679.17 1283.33 1683.33C1287.5 1683.33 1291.67 1683.33 1295.83 1679.17C1637.5 1558.33 1816.67 1183.33 1695.83 841.667C1620.83 633.333 1458.33 470.833 1250 395.833Z" fill="white" />
      </svg>
    );
  }

  if (!isUsdt) {
    return (
      <div
        className="bg-surface-3 flex size-5 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
        aria-label={symbol}
      >
        {symbol.charAt(0)}
      </div>
    );
  }

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="size-5 flex-shrink-0"
      aria-label={symbol}
    >
      <rect width="24" height="24" rx="12" fill="#50AF95" />
      <g clipPath="url(#usdt-clip)" fillRule="evenodd" clipRule="evenodd">
        <path
          d="M4.394 2.103.018 11.294a.178.178 0 00.039.208l11.818 11.325a.18.18 0 00.25 0l11.819-11.324a.179.179 0 00.038-.208l-4.376-9.192A.176.176 0 0019.444 2H4.557a.177.177 0 00-.163.103Z"
          fill="#50AF95"
        />
        <path
          d="M13.518 12.238c-.084.007-.523.033-1.5.033-.778 0-1.33-.024-1.524-.033-3.006-.132-5.25-.655-5.25-1.282 0-.626 2.244-1.149 5.25-1.283v2.044c.196.014.759.048 1.537.048.933 0 1.4-.04 1.485-.047V9.675c2.999.133 5.238.656 5.238 1.281 0 .626-2.238 1.149-5.238 1.282h.002Zm0-2.775v-1.83h4.186V4.846H6.308v2.789h4.185v1.828c-3.402.157-5.96.83-5.96 1.638 0 .807 2.558 1.48 5.96 1.638v5.861h3.025v-5.864c3.394-.156 5.948-.829 5.948-1.636 0-.806-2.552-1.48-5.948-1.637v.001Z"
          fill="#fff"
        />
      </g>
      <defs>
        <clipPath id="usdt-clip">
          <path fill="#fff" d="M0 2h24v20.878H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}
